import React, { useState, useEffect, useMemo } from 'react';
import { Redirect } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import Loading from '../ui/Loading.js';
import Security from '../../commons/security/Security.js';
import * as Utils from '../../commons/utils/Utils';
import backendStrings from '../../lang/backendStrings.js';
import ProgramasPreventivosGrillaVehiculos from './ProgramasPreventivosGrillaVehiculos.jsx';
import ProgramasPreventivosTicketAbiertoModal from './ProgramasPreventivosTicketAbiertoModal.jsx';

const FORM_INIT = {
    nombre: '',
    fecha_inicio: '',
    fecha_fin: '',
    fecha_limite_publicacion: '',
};

const CATALOGO_REGLAS = [
    { key: 'delta_critico',           labelId: 'programasPreventivosAbm.regla_delta_critico',           defaultMessage: 'Delta crítico (más cercano a vencer va primero)' },
    { key: 'alto_costo_correctivos',  labelId: 'programasPreventivosAbm.regla_alto_costo_correctivos',  defaultMessage: 'Alto costo de correctivos (mayor gasto reciente va primero)' },
    { key: 'antiguedad_vehiculo',     labelId: 'programasPreventivosAbm.regla_antiguedad_vehiculo',     defaultMessage: 'Antigüedad del vehículo (más antiguo va primero)' },
    { key: 'tiempo_sin_preventivo',   labelId: 'programasPreventivosAbm.regla_tiempo_sin_preventivo',   defaultMessage: 'Tiempo sin preventivo (más días sin mantenimiento va primero)' },
    { key: 'garantia',                labelId: 'programasPreventivosAbm.regla_garantia',                defaultMessage: 'Vehículo en garantía (posterga al final)' },
];

const ProgramasPreventivosAbm = (props) => {
    const intl = useIntl();

    const [redirectTo, setRedirectTo] = useState(null);
    const [paso, setPaso] = useState(1);

    // Paso 1 — datos del período
    const [formData, setFormData] = useState({ ...FORM_INIT });

    // Paso 2 — tipos de vehículo
    const [tiposDisponibles, setTiposDisponibles] = useState([]);
    const [tiposSeleccionados, setTiposSeleccionados] = useState({});
    const [presupuestos, setPresupuestos] = useState({});
    const [loadingTipos, setLoadingTipos] = useState(false);
    const [filtroTipo, setFiltroTipo] = useState('');

    // Paso 3 — reglas de priorización: { [tipoId]: [ { key, anioGarantia } ] } en orden
    const [reglasPorTipo, setReglasPorTipo] = useState({});
    const [tipoActivoPaso3, setTipoActivoPaso3] = useState(null);

    // Paso 4 — exclusiones marcadas en el preview, todavía no persistidas:
    // { [modeloTipoId]: { [movilId]: { motivo, comentario } } }
    const [exclusionesPreview, setExclusionesPreview] = useState({});
    // Paso 4 — ediciones de fecha sugerida marcadas en el preview, todavía no persistidas:
    // { [modeloTipoId]: { [movilId]: 'YYYY-MM-DD' } }
    const [edicionesFechaPreview, setEdicionesFechaPreview] = useState({});


    // Estado compartido
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [ticketAbiertoModal, setTicketAbiertoModal] = useState({ open: false, cantidad: 0 });

    useEffect(() => {
        if (!Security.hasPermission('PROGRAMAS_PREVENTIVOS_CREAR')) {
            setRedirectTo('/error');
        }
    }, []);

    useEffect(() => {
        if (paso === 2) {
            setLoadingTipos(true);
            Utils.getData('/programas-preventivos/tipos-vehiculo')
                .then(function(data) {
                    setTiposDisponibles(data || []);
                })
                .catch(function() {
                    setTiposDisponibles([]);
                })
                .finally(function() {
                    setLoadingTipos(false);
                });
        }
        if (paso === 3) {
            var ids = Object.keys(tiposSeleccionados);
            if (ids.length > 0 && !tipoActivoPaso3) {
                setTipoActivoPaso3(parseInt(ids[0], 10));
            }
        }
    }, [paso]);

    // ── Paso 1 ──

    const handleChange = (e) => {
        var name = e.target.name;
        var value = e.target.value;
        setFormData(function(prev) {
            var next = Object.assign({}, prev);
            next[name] = value;
            if (name === 'fecha_inicio') {
                next.fecha_fin = '';
            }
            return next;
        });
    };

    const handleAvanzarPaso2 = (e) => {
        e.preventDefault();
        setErrors([]);
        setLoading(true);
        var q = '/programas-preventivos/validar-periodo?fecha_inicio=' + formData.fecha_inicio + '&fecha_fin=' + formData.fecha_fin;
        if (formData.fecha_limite_publicacion) {
            q += '&fecha_limite_publicacion=' + formData.fecha_limite_publicacion;
        }
        Utils.getDataNoErrorMessage(q)
            .then(function() {
                setPaso(2);
            })
            .catch(function(err) {
                var data = err && err.response && err.response.data;
                var errs = data && data.detalle;
                if (Array.isArray(errs) && errs.length > 0) {
                    setErrors(errs);
                } else if (typeof errs === 'string') {
                    setErrors([errs]);
                } else {
                    setErrors(['errors.default']);
                }
                window.scrollTo(0, 0);
            })
            .finally(function() {
                setLoading(false);
            });
    };

    var paso1Valido = formData.nombre.trim() !== '' &&
        formData.fecha_inicio !== '' &&
        formData.fecha_fin !== '';

    // ── Paso 2 ──

    const handleToggleTipo = (tipoId) => {
        setTiposSeleccionados(function(prev) {
            var next = Object.assign({}, prev);
            if (next[tipoId]) {
                delete next[tipoId];
                setPresupuestos(function(p) {
                    var np = Object.assign({}, p);
                    delete np[tipoId];
                    return np;
                });
            } else {
                next[tipoId] = true;
            }
            return next;
        });
    };

    const handlePresupuesto = (tipoId, value) => {
        setPresupuestos(function(prev) {
            var next = Object.assign({}, prev);
            next[tipoId] = value;
            return next;
        });
    };

    var paso2Valido = Object.keys(tiposSeleccionados).length > 0;

    var tiposFiltrados = tiposDisponibles
        .filter(function(t) {
            return t.nombre.toLowerCase().indexOf(filtroTipo.toLowerCase()) !== -1;
        })
        .sort(function(a, b) {
            var asel = !!tiposSeleccionados[a.id];
            var bsel = !!tiposSeleccionados[b.id];
            if (asel && !bsel) return -1;
            if (!asel && bsel) return 1;
            return 0;
        });

    const handleAvanzarPaso3 = (e) => {
        e.preventDefault();
        setErrors([]);
        var ids = Object.keys(tiposSeleccionados);
        setTipoActivoPaso3(parseInt(ids[0], 10));
        setPaso(3);
    };

    // ── Paso 3 ──

    var tiposSeleccionadosArr = tiposDisponibles.filter(function(t) {
        return !!tiposSeleccionados[t.id];
    });

    const getReglasActivas = (tipoId) => {
        return reglasPorTipo[tipoId] || [];
    };

    const isReglaActiva = (tipoId, key) => {
        return getReglasActivas(tipoId).some(function(r) { return r.key === key; });
    };

    const handleToggleRegla = (tipoId, key) => {
        setReglasPorTipo(function(prev) {
            var reglas = (prev[tipoId] || []).slice();
            var idx = reglas.findIndex(function(r) { return r.key === key; });
            if (idx !== -1) {
                reglas.splice(idx, 1);
            } else {
                reglas.push({ key: key, anioGarantia: '' });
            }
            var next = Object.assign({}, prev);
            next[tipoId] = reglas;
            return next;
        });
    };

    const handleMoverRegla = (tipoId, idx, direccion) => {
        setReglasPorTipo(function(prev) {
            var reglas = (prev[tipoId] || []).slice();
            var nuevoIdx = idx + direccion;
            if (nuevoIdx < 0 || nuevoIdx >= reglas.length) return prev;
            var temp = reglas[idx];
            reglas[idx] = reglas[nuevoIdx];
            reglas[nuevoIdx] = temp;
            var next = Object.assign({}, prev);
            next[tipoId] = reglas;
            return next;
        });
    };

    const handleAnioGarantia = (tipoId, value) => {
        setReglasPorTipo(function(prev) {
            var reglas = (prev[tipoId] || []).map(function(r) {
                if (r.key === 'garantia') return Object.assign({}, r, { anioGarantia: value });
                return r;
            });
            var next = Object.assign({}, prev);
            next[tipoId] = reglas;
            return next;
        });
    };

    // ── Construcción del payload (compartida por preview y guardado) ──

    const construirTipoInput = (tipoId) => {
        var t = { modelo_tipo_id: tipoId };
        if (presupuestos[tipoId] !== undefined && presupuestos[tipoId] !== '') {
            t.presupuesto_referencia = presupuestos[tipoId];
        }
        var reglas = getReglasActivas(tipoId);
        t.reglas = reglas.map(function(r, i) {
            var obj = { regla: r.key, orden: i + 1 };
            if (r.key === 'garantia' && r.anioGarantia !== '') {
                obj.anio_garantia = parseInt(r.anioGarantia, 10);
            }
            return obj;
        });
        return t;
    };

    const construirTipos = () => {
        return Object.keys(tiposSeleccionados).map(function(id) {
            return construirTipoInput(parseInt(id, 10));
        });
    };

    // ── Avanzar a paso 4 (preview, no guarda nada) ──

    const handleAvanzarPaso4 = (e) => {
        e.preventDefault();
        setErrors([]);
        setPaso(4);
    };

    // ── Paso 4 — preview de la grilla (sin persistir): delega el fetch/filtros/orden en
    // ProgramasPreventivosGrillaVehiculos vía la prop previewTipoInput. Memoizado para que solo
    // dispare un refetch cuando cambian las reglas/presupuesto del tipo activo, no en cada render.
    var reglasTipoActivoKey = tipoActivoPaso3 ? JSON.stringify(getReglasActivas(tipoActivoPaso3)) : '';
    var presupuestoTipoActivo = tipoActivoPaso3 ? presupuestos[tipoActivoPaso3] : undefined;
    const previewTipoInput = useMemo(function() {
        if (!tipoActivoPaso3) return null;
        return construirTipoInput(tipoActivoPaso3);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tipoActivoPaso3, reglasTipoActivoKey, presupuestoTipoActivo]);

    const handleCambiarTabGrilla = (tipoId) => {
        setTipoActivoPaso3(tipoId);
    };

    const handleVolverPaso3DesdeGrilla = () => {
        setErrors([]);
        setPaso(3);
    };

    const handleExcluirPreview = (modeloTipoId, movilId, motivoId, comentario, motivoNombre) => {
        setExclusionesPreview(function(prev) {
            var next = Object.assign({}, prev);
            next[modeloTipoId] = Object.assign({}, next[modeloTipoId], {
                [movilId]: { motivo: motivoId, motivoNombre: motivoNombre, comentario: comentario },
            });
            return next;
        });
    };

    const handleReincluirPreview = (modeloTipoId, movilId) => {
        setExclusionesPreview(function(prev) {
            var tipoMap = Object.assign({}, prev[modeloTipoId]);
            delete tipoMap[movilId];
            var next = Object.assign({}, prev);
            next[modeloTipoId] = tipoMap;
            return next;
        });
    };

    const handleEditarFechaPreview = (modeloTipoId, movilId, nuevaFecha) => {
        setEdicionesFechaPreview(function(prev) {
            var next = Object.assign({}, prev);
            next[modeloTipoId] = Object.assign({}, next[modeloTipoId], { [movilId]: nuevaFecha });
            return next;
        });
    };

    // ── Guardar (persiste todo junto: programa + tipos + reglas + grilla generada) ──

    const handleGuardarFinal = () => {
        setErrors([]);

        var payload = {
            nombre:       formData.nombre,
            fecha_inicio: formData.fecha_inicio,
            fecha_fin:    formData.fecha_fin,
            tipos:        construirTipos(),
        };
        if (formData.fecha_limite_publicacion) {
            payload.fecha_limite_publicacion = formData.fecha_limite_publicacion;
        }

        setLoading(true);
        var conTicketAbierto = 0;
        Utils.sendDataNoErrorMessage('/programas-preventivos', 'POST', payload)
            .then(function(data) {
                return Utils.sendData('/programas-preventivos/' + data.id + '/generar', 'POST', {});
            })
            .then(function(generado) {
                conTicketAbierto = generado.con_ticket_abierto || 0;

                // Exclusiones marcadas en el preview (VEC-3333 extendido): recién ahora existen filas
                // reales en programa_preventivo_vehiculos, se aplican una por una (VEC-3333 excluye
                // explícitamente la exclusión masiva en una sola llamada).
                var pendientes = [];
                (generado.tipos || []).forEach(function(tipo) {
                    var exclusionesTipo = exclusionesPreview[tipo.modelo_tipo_id] || {};
                    Object.keys(exclusionesTipo).forEach(function(movilId) {
                        var ex = exclusionesTipo[movilId];
                        pendientes.push(
                            Utils.sendData(
                                '/programa-preventivo-tipos/' + tipo.programa_preventivo_tipo_id + '/vehiculos-por-movil/' + movilId + '/excluir',
                                'POST',
                                { motivo: ex.motivo, comentario: ex.comentario }
                            )
                        );
                    });

                    // Ediciones de fecha marcadas en el preview (VEC-3335 extendido), mismo mecanismo:
                    // se aplican recién ahora que existen filas reales, una por una.
                    var edicionesTipo = edicionesFechaPreview[tipo.modelo_tipo_id] || {};
                    Object.keys(edicionesTipo).forEach(function(movilId) {
                        pendientes.push(
                            Utils.sendData(
                                '/programa-preventivo-tipos/' + tipo.programa_preventivo_tipo_id + '/vehiculos-por-movil/' + movilId + '/fecha-sugerida',
                                'POST',
                                { fecha_sugerida: edicionesTipo[movilId] }
                            )
                        );
                    });
                });
                return Promise.all(pendientes);
            })
            .then(function() {
                if (conTicketAbierto > 0) {
                    setTicketAbiertoModal({ open: true, cantidad: conTicketAbierto });
                } else {
                    setRedirectTo('/programas-preventivos');
                }
            })
            .catch(function(err) {
                var data = err && err.response && err.response.data;
                var errs = data && data.detalle;
                if (Array.isArray(errs) && errs.length > 0) {
                    setErrors(errs);
                } else {
                    setErrors(['errors.default']);
                }
                window.scrollTo(0, 0);
            })
            .finally(function() {
                setLoading(false);
            });
    };

    // ── Render ──

    return (
        <React.Fragment>
            {redirectTo && <Redirect push to={redirectTo} />}
            {loading && <Loading />}
            <ProgramasPreventivosTicketAbiertoModal
                open={ticketAbiertoModal.open}
                cantidad={ticketAbiertoModal.cantidad}
                onClose={function() {
                    setTicketAbiertoModal({ open: false, cantidad: 0 });
                    setRedirectTo('/programas-preventivos');
                }}
            />
            <div className="row">
                <div className="col-md-12">

                    {errors.length > 0 && (
                        <div className="alert alert-danger" role="alert">
                            {errors.map(function(e, i) {
                                return (
                                    <li key={i}>
                                        <FormattedMessage {...(backendStrings[e] ? backendStrings[e] : backendStrings['errors.default'])} />
                                    </li>
                                );
                            })}
                        </div>
                    )}

                    {/* ── PASO 1 ── */}
                    {paso === 1 && (
                        <form className="form form-horizontal" onSubmit={handleAvanzarPaso2}>
                            <div className="form-body">
                                <div className="card pull-up">
                                    <div className="card-content">
                                        <div className="card-body">
                                            <h4 className="form-section">
                                                <i className="icon-calendar"></i>
                                                <FormattedMessage id="programasPreventivosAbm.header_periodo_operativo" defaultMessage=" Período Operativo" />
                                                <div className="float-right" style={{ fontSize: '14px' }}>
                                                    * <FormattedMessage id="programasPreventivosAbm.campos_requeridos" defaultMessage="campos requeridos" />
                                                </div>
                                            </h4>

                                            <div className="row">
                                                <div className="col-md-8">
                                                    <div className="form-group row">
                                                        <label className="col-md-3 label-control col-form-label" htmlFor="nombre">
                                                            <FormattedMessage id="programasPreventivosAbm.label_nombre" defaultMessage="Nombre" /> *:
                                                        </label>
                                                        <div className="col-md-9">
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                id="nombre"
                                                                name="nombre"
                                                                maxLength={100}
                                                                placeholder={intl.formatMessage({ id: 'programasPreventivosAbm.placeholder_nombre', defaultMessage: 'Nombre del programa' })}
                                                                value={formData.nombre}
                                                                onChange={handleChange}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="row">
                                                <div className="col-md-4">
                                                    <div className="form-group row">
                                                        <label className="col-md-6 label-control col-form-label" htmlFor="fecha_inicio">
                                                            <FormattedMessage id="programasPreventivosAbm.label_fecha_inicio" defaultMessage="Fecha de inicio" /> *:
                                                        </label>
                                                        <div className="col-md-6">
                                                            <input type="date" className="form-control" id="fecha_inicio" name="fecha_inicio" value={formData.fecha_inicio} onChange={handleChange} />
                                                        </div>
                                                    </div>
                                                </div>
                                                {formData.fecha_inicio && (
                                                    <div className="col-md-4">
                                                        <div className="form-group row">
                                                            <label className="col-md-6 label-control col-form-label" htmlFor="fecha_fin">
                                                                <FormattedMessage id="programasPreventivosAbm.label_fecha_fin" defaultMessage="Fecha de fin" /> *:
                                                            </label>
                                                            <div className="col-md-6">
                                                                <input type="date" className="form-control" id="fecha_fin" name="fecha_fin" min={formData.fecha_inicio} value={formData.fecha_fin} onChange={handleChange} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="row">
                                                <div className="col-md-4">
                                                    <div className="form-group row">
                                                        <label className="col-md-6 label-control col-form-label" htmlFor="fecha_limite_publicacion">
                                                            <FormattedMessage id="programasPreventivosAbm.label_fecha_limite" defaultMessage="Fecha límite de publicación" />:
                                                        </label>
                                                        <div className="col-md-6">
                                                            <input type="date" className="form-control" id="fecha_limite_publicacion" name="fecha_limite_publicacion" min={formData.fecha_inicio} value={formData.fecha_limite_publicacion} onChange={handleChange} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="card pull-up">
                                    <div className="card-content">
                                        <div className="card-body">
                                            <div className="text-cd text-right">
                                                <button type="submit" className="btn btn-primary mr-1" disabled={!paso1Valido}>
                                                    <i className="fa fa-arrow-circle-right"></i>{' '}
                                                    <FormattedMessage id="programasPreventivosAbm.btn_siguiente" defaultMessage="Siguiente" />
                                                </button>
                                                <button type="button" className="btn btn-danger" onClick={function() { setRedirectTo('/programas-preventivos'); }}>
                                                    <i className="fa fa-times-circle"></i>{' '}
                                                    <FormattedMessage id="programasPreventivosAbm.btn_cancelar" defaultMessage="Cancelar" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    )}

                    {/* ── PASO 2 ── */}
                    {paso === 2 && (
                        <form className="form form-horizontal" onSubmit={handleAvanzarPaso3}>
                            <div className="form-body">
                                <div className="card pull-up">
                                    <div className="card-content">
                                        <div className="card-body">
                                            <h4 className="form-section">
                                                <i className="icon-car"></i>
                                                <FormattedMessage id="programasPreventivosAbm.header_tipos_vehiculo" defaultMessage=" Tipos de vehículo" />
                                                <div className="float-right" style={{ fontSize: '14px' }}>
                                                    * <FormattedMessage id="programasPreventivosAbm.campos_requeridos" defaultMessage="campos requeridos" />
                                                </div>
                                            </h4>

                                            <p className="text-muted">
                                                <FormattedMessage id="programasPreventivosAbm.desc_tipos" defaultMessage="Seleccioná los tipos de vehículo que participan del programa. Podés asignar un presupuesto de referencia opcional por tipo." />
                                            </p>

                                            {loadingTipos ? (
                                                <div className="text-center p-3">
                                                    <i className="fa fa-spinner fa-spin fa-2x"></i>
                                                </div>
                                            ) : (
                                                <React.Fragment>
                                                    <div className="row mb-1">
                                                        <div className="col-md-4">
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                placeholder={intl.formatMessage({ id: 'programasPreventivosAbm.placeholder_buscar_tipo', defaultMessage: 'Buscar tipo...' })}
                                                                value={filtroTipo}
                                                                onChange={function(e) { setFiltroTipo(e.target.value); }}
                                                            />
                                                        </div>
                                                        <div className="col-md-8 d-flex align-items-center">
                                                            <small className="text-muted">
                                                                {Object.keys(tiposSeleccionados).length} <FormattedMessage id="programasPreventivosAbm.seleccionados" defaultMessage="seleccionados" />
                                                            </small>
                                                        </div>
                                                    </div>
                                                    <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
                                                        <table className="table table-hover mb-0">
                                                            <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                                                                <tr>
                                                                    <th style={{ width: '40px' }}></th>
                                                                    <th><FormattedMessage id="programasPreventivosAbm.col_tipo" defaultMessage="Tipo de vehículo" /></th>
                                                                    <th style={{ width: '160px' }} className="text-center"><FormattedMessage id="programasPreventivosAbm.col_activos" defaultMessage="Activos" /></th>
                                                                    <th style={{ width: '220px' }}><FormattedMessage id="programasPreventivosAbm.col_presupuesto" defaultMessage="Presupuesto de referencia" /></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {tiposFiltrados.length === 0 ? (
                                                                    <tr>
                                                                        <td colSpan={4} className="text-center text-muted p-3">
                                                                            <FormattedMessage id="programasPreventivosAbm.sin_tipos" defaultMessage="No hay tipos de vehículo disponibles." />
                                                                        </td>
                                                                    </tr>
                                                                ) : (
                                                                    tiposFiltrados.map(function(tipo) {
                                                                        var seleccionado = !!tiposSeleccionados[tipo.id];
                                                                        return (
                                                                            <tr key={tipo.id} onClick={function() { handleToggleTipo(tipo.id); }} style={{ cursor: 'pointer', background: seleccionado ? '#f0f8ff' : '' }}>
                                                                                <td>
                                                                                    <input type="checkbox" checked={seleccionado} onChange={function() { handleToggleTipo(tipo.id); }} onClick={function(e) { e.stopPropagation(); }} />
                                                                                </td>
                                                                                <td>{tipo.nombre}</td>
                                                                                <td className="text-center">
                                                                                    <span className="badge badge-secondary">{tipo.cantidad_activos}</span>
                                                                                </td>
                                                                                <td onClick={function(e) { e.stopPropagation(); }}>
                                                                                    {seleccionado && (
                                                                                        <input
                                                                                            type="number"
                                                                                            className="form-control form-control-sm"
                                                                                            min="0"
                                                                                            step="0.01"
                                                                                            placeholder={intl.formatMessage({ id: 'programasPreventivosAbm.placeholder_presupuesto', defaultMessage: 'Opcional' })}
                                                                                            value={presupuestos[tipo.id] || ''}
                                                                                            onChange={function(e) { handlePresupuesto(tipo.id, e.target.value); }}
                                                                                        />
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </React.Fragment>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="card pull-up">
                                    <div className="card-content">
                                        <div className="card-body">
                                            <div className="text-cd text-right">
                                                <button type="button" className="btn btn-secondary mr-1" onClick={function() { setPaso(1); setErrors([]); }}>
                                                    <i className="fa fa-arrow-circle-left"></i>{' '}
                                                    <FormattedMessage id="programasPreventivosAbm.btn_anterior" defaultMessage="Anterior" />
                                                </button>
                                                <button type="submit" className="btn btn-primary mr-1" disabled={!paso2Valido}>
                                                    <i className="fa fa-arrow-circle-right"></i>{' '}
                                                    <FormattedMessage id="programasPreventivosAbm.btn_siguiente" defaultMessage="Siguiente" />
                                                </button>
                                                <button type="button" className="btn btn-danger" onClick={function() { setRedirectTo('/programas-preventivos'); }}>
                                                    <i className="fa fa-times-circle"></i>{' '}
                                                    <FormattedMessage id="programasPreventivosAbm.btn_cancelar" defaultMessage="Cancelar" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    )}

                    {/* ── PASO 3 ── */}
                    {paso === 3 && (
                        <form className="form form-horizontal" onSubmit={handleAvanzarPaso4}>
                            <div className="form-body">
                                <div className="card pull-up">
                                    <div className="card-content">
                                        <div className="card-body">
                                            <h4 className="form-section">
                                                <i className="fa fa-sort-amount-asc"></i>
                                                <FormattedMessage id="programasPreventivosAbm.header_priorizacion" defaultMessage=" Reglas de priorización" />
                                            </h4>

                                            <p className="text-muted">
                                                <FormattedMessage id="programasPreventivosAbm.desc_priorizacion" defaultMessage="Configurá las reglas que ordenarán los vehículos en la grilla para cada tipo. Las reglas se aplican en cascada según el orden definido. Este paso es opcional." />
                                            </p>

                                            {/* Tabs de tipos */}
                                            <ul className="nav nav-tabs mb-3">
                                                {tiposSeleccionadosArr.map(function(tipo) {
                                                    var activo = tipoActivoPaso3 === tipo.id;
                                                    var cantReglas = getReglasActivas(tipo.id).length;
                                                    return (
                                                        <li key={tipo.id} className="nav-item">
                                                            <a
                                                                className={'nav-link' + (activo ? ' active' : '')}
                                                                href="#!"
                                                                onClick={function(e) { e.preventDefault(); setTipoActivoPaso3(tipo.id); }}
                                                            >
                                                                {tipo.nombre}
                                                                {cantReglas > 0 && (
                                                                    <span className="badge badge-primary ml-1">{cantReglas}</span>
                                                                )}
                                                            </a>
                                                        </li>
                                                    );
                                                })}
                                            </ul>

                                            {tipoActivoPaso3 && (function() {
                                                var tipoId = tipoActivoPaso3;
                                                var reglasActivas = getReglasActivas(tipoId);

                                                return (
                                                    <div className="row">
                                                        {/* Catálogo — izquierda */}
                                                        <div className="col-md-6">
                                                            <h6 className="text-muted mb-2">
                                                                <FormattedMessage id="programasPreventivosAbm.catalogo_titulo" defaultMessage="Reglas disponibles" />
                                                            </h6>
                                                            {CATALOGO_REGLAS.map(function(regla) {
                                                                var activa = isReglaActiva(tipoId, regla.key);
                                                                return (
                                                                    <div key={regla.key} className="form-check mb-2">
                                                                        <input
                                                                            type="checkbox"
                                                                            className="form-check-input"
                                                                            id={'regla_' + tipoId + '_' + regla.key}
                                                                            checked={activa}
                                                                            onChange={function() { handleToggleRegla(tipoId, regla.key); }}
                                                                        />
                                                                        <label className="form-check-label" htmlFor={'regla_' + tipoId + '_' + regla.key}>
                                                                            <FormattedMessage id={regla.labelId} defaultMessage={regla.defaultMessage} />
                                                                        </label>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>

                                                        {/* Orden activo — derecha */}
                                                        <div className="col-md-6">
                                                            <h6 className="text-muted mb-2">
                                                                <FormattedMessage id="programasPreventivosAbm.orden_titulo" defaultMessage="Orden de aplicación" />
                                                            </h6>
                                                            {reglasActivas.length === 0 ? (
                                                                <p className="text-muted">
                                                                    <FormattedMessage id="programasPreventivosAbm.sin_reglas" defaultMessage="Sin reglas activas — la grilla no tendrá un orden de priorización específico." />
                                                                </p>
                                                            ) : (
                                                                <div>
                                                                    {reglasActivas.map(function(regla, idx) {
                                                                        var info = CATALOGO_REGLAS.find(function(r) { return r.key === regla.key; });
                                                                        return (
                                                                            <div key={regla.key} className="d-flex align-items-center mb-2 p-2" style={{ background: '#f8f9fa', borderRadius: '4px' }}>
                                                                                <span className="badge badge-primary mr-2" style={{ minWidth: '24px' }}>{idx + 1}</span>
                                                                                <span style={{ fontSize: '13px' }}>
                                                                                    {info ? <FormattedMessage id={info.labelId} defaultMessage={info.defaultMessage} /> : regla.key}
                                                                                </span>
                                                                                {regla.key === 'garantia' && (
                                                                                    <input
                                                                                        type="number"
                                                                                        className="form-control form-control-sm ml-2"
                                                                                        style={{ width: '100px' }}
                                                                                        min="2000"
                                                                                        max="2100"
                                                                                        placeholder="Ej: 2022"
                                                                                        value={regla.anioGarantia || ''}
                                                                                        onClick={function(e) { e.stopPropagation(); }}
                                                                                        onChange={function(e) { handleAnioGarantia(tipoId, e.target.value); }}
                                                                                    />
                                                                                )}
                                                                                <div className="ml-auto pl-2">
                                                                                    <button type="button" className="btn btn-sm btn-light py-0 px-1 mr-1" disabled={idx === 0} onClick={function() { handleMoverRegla(tipoId, idx, -1); }}>
                                                                                        <i className="fa fa-chevron-up"></i>
                                                                                    </button>
                                                                                    <button type="button" className="btn btn-sm btn-light py-0 px-1" disabled={idx === reglasActivas.length - 1} onClick={function() { handleMoverRegla(tipoId, idx, 1); }}>
                                                                                        <i className="fa fa-chevron-down"></i>
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </div>

                                <div className="card pull-up">
                                    <div className="card-content">
                                        <div className="card-body">
                                            <div className="text-cd text-right">
                                                <button type="button" className="btn btn-secondary mr-1" onClick={function() { setPaso(2); setErrors([]); }}>
                                                    <i className="fa fa-arrow-circle-left"></i>{' '}
                                                    <FormattedMessage id="programasPreventivosAbm.btn_anterior" defaultMessage="Anterior" />
                                                </button>
                                                <button type="submit" className="btn btn-primary mr-1" disabled={loading}>
                                                    <i className="fa fa-arrow-circle-right"></i>{' '}
                                                    <FormattedMessage id="programasPreventivosAbm.btn_siguiente" defaultMessage="Siguiente" />
                                                </button>
                                                <button type="button" className="btn btn-danger" onClick={function() { setRedirectTo('/programas-preventivos'); }}>
                                                    <i className="fa fa-times-circle"></i>{' '}
                                                    <FormattedMessage id="programasPreventivosAbm.btn_cancelar" defaultMessage="Cancelar" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    )}

                    {/* ── PASO 4 — Preview de la grilla (no persiste nada hasta "Guardar") ── */}
                    {paso === 4 && (
                        <div className="form-body">
                            <div className="card pull-up">
                                <div className="card-content">
                                    <div className="card-body">
                                        <h4 className="form-section">
                                            <i className="fa fa-table"></i>
                                            <FormattedMessage id="programasPreventivosAbm.header_grilla" defaultMessage=" Preview de la grilla de vehículos" />
                                        </h4>

                                        <p className="text-muted">
                                            <FormattedMessage
                                                id="programasPreventivosAbm.desc_grilla"
                                                defaultMessage="Vista previa según los vehículos activos con preventivo configurado y las reglas definidas. Nada se guarda todavía — recién se persiste al confirmar Guardar."
                                            />
                                        </p>

                                        <ul className="nav nav-tabs mb-3">
                                            {tiposSeleccionadosArr.map(function(tipo) {
                                                var activo = tipoActivoPaso3 === tipo.id;
                                                return (
                                                    <li key={tipo.id} className="nav-item">
                                                        <a
                                                            className={'nav-link' + (activo ? ' active' : '')}
                                                            href="#!"
                                                            onClick={function(e) { e.preventDefault(); handleCambiarTabGrilla(tipo.id); }}
                                                        >
                                                            {tipo.nombre}
                                                        </a>
                                                    </li>
                                                );
                                            })}
                                        </ul>

                                        <ProgramasPreventivosGrillaVehiculos
                                            previewTipoInput={previewTipoInput}
                                            exclusionesPreview={tipoActivoPaso3 ? (exclusionesPreview[tipoActivoPaso3] || {}) : {}}
                                            onExcluirPreview={handleExcluirPreview}
                                            onReincluirPreview={handleReincluirPreview}
                                            edicionesFechaPreview={tipoActivoPaso3 ? (edicionesFechaPreview[tipoActivoPaso3] || {}) : {}}
                                            onEditarFechaPreview={handleEditarFechaPreview}
                                            periodoOperativo={{ fecha_inicio: formData.fecha_inicio, fecha_fin: formData.fecha_fin }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="card pull-up">
                                <div className="card-content">
                                    <div className="card-body">
                                        <div className="text-cd text-right">
                                            <button type="button" className="btn btn-secondary mr-1" onClick={handleVolverPaso3DesdeGrilla}>
                                                <i className="fa fa-arrow-circle-left"></i>{' '}
                                                <FormattedMessage id="programasPreventivosAbm.btn_anterior" defaultMessage="Anterior" />
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-primary mr-1"
                                                disabled={loading}
                                                onClick={handleGuardarFinal}
                                            >
                                                <i className="fa fa-check-circle"></i>{' '}
                                                <FormattedMessage id="programasPreventivosAbm.btn_guardar" defaultMessage="Guardar" />
                                            </button>
                                            <button type="button" className="btn btn-danger" onClick={function() { setRedirectTo('/programas-preventivos'); }}>
                                                <i className="fa fa-times-circle"></i>{' '}
                                                <FormattedMessage id="programasPreventivosAbm.btn_cancelar" defaultMessage="Cancelar" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </React.Fragment>
    );
};

export default ProgramasPreventivosAbm;
